import { $t } from '@/i18n';
import { pageScrollTo, pageScrollToTop, pageScrollToBottom, getContentClientHeight } from '@utils/browser';
import {
  XyPageSelectorMeta,
  XyPageletPropsEditorMeta,
} from '../paramsFormComponents';
import { getRouterPublicParams } from '@utils/routerPublicParams';
import { normalizeRouteConfig, operatePageUrl, getBaseAppPath } from '@utils/path';
import qs from 'query-string';
import { message } from '@utils/wuji-message';
import { CATEGORY } from './const';
import { isRuntime as isRuntimeFn } from '@/utils/globalInfo';
import { useRouter } from '@/router/useRouter';

const isValidUrl = (url) => {
  try {
    new URL(url);
  } catch (error) {
    return false;
  }
  return true;
};

// 滚动类型处理函数
const SCROLL_TYPE_STRATEGY = {
  top: (params) => {
    const { behavior } = params;
    pageScrollToTop({ behavior });
  },
  bottom: (params) => {
    const { behavior } = params;
    pageScrollToBottom({ behavior });
  },
  y: (params) => {
    const { yCoord, behavior } = params;
    pageScrollTo({ top: yCoord, behavior });
  },
  component: (params, ctx) => {
    const { behavior, componentId, compOffsetType = 'start', compOffset = 0 } = params;
    let { renderer } = ctx;
    let compPath = '';
    // 跨页面片的情况, 需要切换 renderer
    ({ renderer, compPath } = renderer?.ucQuerySelector(componentId) ?? {});
    const target = renderer?.getRefByPath(compPath)?.$el;
    // 开发环境提示
    if (!target && componentId && renderer?.w?.env === 'dev') {
      return message.warning($t('k_7fdf389', { arg_0: componentId }));
    }

    if (!target) return;

    if (!compOffset) {
      target.scrollIntoView({ behavior, block: compOffsetType });
    } else {
      const layoutInstance = window.xy_runtime_default_layout_instance;
      const { offsetTop } = target;

      // 顶部导航偏移量
      const isNvaFixTop = !!layoutInstance?.isNavFixTop;
      const navHeight = layoutInstance?.isShowNav ? parseInt(layoutInstance?.headerHeight ?? 0, 10) : 0;

      // 计算实际滚动高度
      let computedTop = offsetTop;
      if (compOffsetType === 'start') {
        computedTop = offsetTop - compOffset + (isNvaFixTop ? 0 : navHeight);
      } else {
        const contentHeight = getContentClientHeight();
        computedTop = offsetTop - contentHeight + target.offsetHeight + navHeight + compOffset;
      }

      pageScrollTo({ top: computedTop, behavior });
    }
  },
};

// 滚动页面
export const pageScrollAtom = {
  id: 'xy:pageScroll',
  name: $t('k_95ef785e'),
  category: CATEGORY.NAVIGATOR,
  paramsSchema: {
    type: 'object',
    fields: [
      {
        id: 'type',
        type: 'string',
        title: $t('k_3e45c1a2'),
        enum: [
          { value: 'top', label: $t('k_e6d681e1') },
          { value: 'bottom', label: $t('k_e20c63be') },
          { value: 'y', label: $t('k_43368a95') },
          { value: 'component', label: $t('k_b00dff6') },
        ],
        default: 'top',
      },
      {
        id: 'yCoord',
        type: 'number',
        title: $t('k_b0e1bf8a'),
        condition: 'params.type === "y"',
        default: 0,
      },
      {
        id: 'componentId',
        type: 'string',
        title: $t('k_305b32b3'),
        condition: 'params.type === "component"',
        ui: {
          type: 'compSelectorInput',
        },
      },
      {
        id: 'compOffsetType',
        type: 'string',
        title: $t('k_3c4a03fc'),
        condition: 'params.type === "component"',
        enum: [
          { value: 'start', label: $t('k_e6d681e1') },
          { value: 'end', label: $t('k_e20c63be') },
        ],
        default: 'start',
      },
      {
        id: 'compOffset',
        type: 'number',
        title: $t('k_f69a92ff'),
        condition: 'params.type === "component"',
        default: 0,
      },
      {
        id: 'behavior',
        type: 'string',
        title: $t('k_2ec227fd'),
        enum: [
          { value: 'instant', label: $t('k_4adfec41') },
          { value: 'smooth', label: $t('k_bff2dc8a') },
        ],
        default: 'instant',
      },
    ],
  },
  execute: async (ctx, params) => SCROLL_TYPE_STRATEGY[params?.type]?.call(null, params, ctx),
};
// 跳转页面
export const openPageAtom = {
  id: 'xy:openPage',
  name: $t('k_c8131ca6'),
  category: CATEGORY.NAVIGATOR,
  paramsSchema: {
    type: 'object',
    fields: [
      {
        id: 'pageId',
        type: 'string',
        title: $t('k_74fc2ea3'),
        ui: { type: 'XyPageSelector' },
      },
      {
        id: 'data',
        type: 'array',
        title: $t('k_15980a00'),
        'uc:allowInterop': true,
        ui: {
          type: 'XyPageletPropsEditor',
          props: {
            keyIsInteropable: true,
          },
        },
        items: {
          type: 'object',
          fields: [
            { id: 'key', type: 'string', ui: { type: 'exprInput', props: { type: 'interopable-string' } } },
            { id: 'value', type: 'string', ui: { type: 'exprInput', props: { type: 'interopable-string' } } },
          ],
        },
      },
      {
        id: 'paramsData',
        type: 'array',
        title: $t('k_38a133a4'),
        'uc:allowInterop': true,
        ui: {
          type: 'XyPageletPropsEditor',
          props: {
            keyIsInteropable: true,
          },
        },
        items: {
          type: 'object',
          fields: [
            { id: 'key', type: 'string', ui: { type: 'exprInput', props: { type: 'interopable-string' } } },
            { id: 'value', type: 'string', ui: { type: 'exprInput', props: { type: 'interopable-string' } } },
          ],
        },
      },
      { id: 'newWindow', type: 'boolean', title: $t('k_abb410c9'), default: true },
      {
        id: 'method',
        type: 'string',
        title: $t('k_50e3b7e2'),
        default: 'push',
        condition: 'params.newWindow !== true',
        enum: [
          { label: $t('k_9866df8a'), value: 'push' },
          { label: $t('k_713c4b44'), value: 'replace' },
        ],
      },
    ],
  },
  paramsFormComponents: [
    XyPageSelectorMeta,
    XyPageletPropsEditorMeta,
  ],
  async execute(ctx, params) {
    const { route, router } = useRouter();
    const { pageId, data, paramsData, newWindow, method = 'push' } = params;

    const isRuntime = isRuntimeFn();

    let projectId = ''; // 页面所属的应用ID
    let pagePath = '';  // 页面真实路径
    if (Array.isArray(window.w?.pageList)) {
      // page.id === pageId 是为了兼容老的版本用了id作为页面选择器的值
      const [page] = window.w.pageList.filter(page => page.id === pageId || page.pageId === pageId);
      if (page) {
        ({ projectId, path: pagePath } = page);
      }
    }

    if (!pagePath) {
      throw new Error($t('k_73f40a65'));
    }

    const query = { ...getRouterPublicParams(route.value.query) };
    if (Array.isArray(data) && data.length > 0) {
      data.forEach((kv) => {
        const { key, value } = kv;
        query[key] = value;
      });
    }
    const queryString = qs.stringify(query);

    const pathParams = {};
    if (Array.isArray(paramsData) && paramsData.length > 0) {
      paramsData.forEach((kv) => {
        const { key, value } = kv;
        pathParams[key] = value;
      });
    }
    const paramsString = qs.stringify(pathParams);

    if (isRuntime) {
      // 运行时
      operatePageUrl(
        { path: pagePath, query: queryString, params: paramsString },
        { open: newWindow, slash: true, method, $router: router.value, $route: route.value },
      );
    } else {
      // 编辑时
      const {
        path: normalizePath,
      } = normalizeRouteConfig({ path: pagePath, params: paramsString, query: queryString, $route: route.value });
      const { branch } = route.value?.query || {};
      // 编辑器预览使用原始应用路径，并保留当前语言前缀。
      let url = getBaseAppPath({ projectId, env: 'dev', branch }, true);
      url += normalizePath;
      if (queryString) url += `?${queryString}`;

      const pageConfig = ctx.uc?.designer?.$parent;
      if (pageConfig?.setOpenLinkPopover) {
        pageConfig.setOpenLinkPopover({
          text: $t('k_463d4473', { arg_0: url }),
          target: url,
        });
      } else {
        window.open(url);
      }
    }
  },
};
// 跳转链接
export const openLinkAtom = {
  id: 'xy:openLink',
  name: $t('k_c8cd5c3d'),
  category: CATEGORY.NAVIGATOR,
  paramsSchema: {
    type: 'object',
    fields: [
      { id: 'url', type: 'string' },
      { id: 'newWindow', type: 'boolean', title: $t('k_abb410c9') },
    ],
  },
  execute(ctx, params) {
    const { url, newWindow } = params;
    if (!url) {
      throw new Error($t('k_6ecc6f31'));
    }
    if ((url.startsWith('http') || url.startsWith('ftp')) && !isValidUrl(url)) {
      throw new Error($t('k_6ecc6f31'));
    }
    // 相对协议
    if (url.startsWith('//') && !isValidUrl(location.protocol + url)) {
      throw new Error($t('k_6ecc6f31'));
    }
    // 相对路径
    if ((url.startsWith('/') && !url.startsWith('//')) && !isValidUrl(location.origin + url)) {
      throw new Error($t('k_6ecc6f31'));
    }

    const pageConfig = ctx.uc?.designer?.$parent;
    if (pageConfig?.setOpenLinkPopover) {
      pageConfig.setOpenLinkPopover({
        text: $t('k_463d4473', { arg_0: url }),
        target: url,
      });
    } else {
      if (newWindow) {
        window.open(url, '_blank');
      } else {
        window.location.href = url;
      }
    }
  },
};
// 返回上一页
export const hitstoryBackAtom = {
  id: 'xy:hitstoryBack',
  name: $t('k_83dee5ca'),
  category: CATEGORY.NAVIGATOR,
  async execute(ctx) {
    const target = () => {
      const { router } = useRouter();
      router.value.go(-1);
    };

    const pageConfig = ctx.uc?.designer?.$parent;
    if (pageConfig?.setOpenLinkPopover) {
      pageConfig.setOpenLinkPopover({
        text: $t('k_679d8bd4'),
        target,
      });
    } else {
      target();
    }
  },
};
// 刷新页面
export const refreshPageAtom = {
  id: 'xy:refreshPage',
  name: $t('k_f74babfc'),
  category: CATEGORY.NAVIGATOR,
  execute(ctx) {
    const target = () => {
      const { router } = useRouter();
      router.value.go(0);
    };

    const pageConfig = ctx.uc?.designer?.$parent;
    if (pageConfig?.setOpenLinkPopover) {
      pageConfig.setOpenLinkPopover({
        text: $t('k_5553721e'),
        target,
      });
    } else {
      target();
    }
  },
};
// 刷新页面片
export const reloadUcRendererAtom = {
  id: 'xy:reloadUcRenderer',
  name: $t('k_60faae12'),
  category: CATEGORY.NAVIGATOR,
  execute(ctx) {
    const { wContext } = ctx.renderer;
    wContext.reloadUcRenderer();
  },
};

export default [
  openPageAtom,
  openLinkAtom,
  hitstoryBackAtom,
  refreshPageAtom,
  reloadUcRendererAtom,
  pageScrollAtom,
];
